import { type Observable, from } from 'rxjs'
import { withLatestFrom, filter, mergeMap, map } from 'rxjs/operators'
import type { PageActivityDescriptor } from '@/infra/page-activity'
import type { SystemNotification } from '@/features/notifications'
import { Id } from '@/infra/id'
import { type Conversation } from '@/features/messaging/Conversation'
import { type Message } from '@/features/messaging/Message'
import { type FetchWorkspaceFn } from '@/features/iam'

/**
 * Options for `incomingJobMessageNotifications`.
 */
export interface IncomingJobMessageNotificationsOptions {
  /**
   * The conversation considered "active". We don't want to display
   * notifications for that conversation unless the page is inactive.
   */
  activeConversation$: Observable<Conversation | null>
  /**
   * All new incoming messages (realtime).
   */
  newMessages$: Observable<Message>
  /**
   * The page's visibility.
   */
  pageActivity$: Observable<PageActivityDescriptor>
  /**
   * Whether the user is active.
   */
  userActive$: Observable<boolean>
  /**
   * Fetches the sender's workspace, whose name the notification's title
   * shows.
   */
  fetchWorkspace: FetchWorkspaceFn
  /**
   * Called when a notification for a job's message is clicked.
   */
  onAction: (options: ActionOptions) => unknown
}

/**
 * What a clicked notification refers to.
 */
export interface ActionOptions {
  jobId: string
}

/**
 * Creates a system notification for each incoming message the user isn't
 * already looking at. Calls are left out because they have their own
 * notifications.
 */
export function incomingJobMessageNotifications({
  activeConversation$,
  newMessages$,
  pageActivity$,
  userActive$,
  fetchWorkspace,
  onAction,
}: IncomingJobMessageNotificationsOptions): Observable<SystemNotification> {
  return newMessages$.pipe(
    filter(
      (message) =>
        message.sender.isCurrentUser === false &&
        message.content.type !== 'Call',
    ),
    withLatestFrom(pageActivity$, userActive$, activeConversation$),
    filter(
      ([message, activity, userActive, convo]) =>
        !(activity.visible && activity.focused && userActive) ||
        convo !== message.conversation,
    ),
    mergeMap(([message]) =>
      // Without the workspace the title lacks its name, which beats losing
      // every later notification to one failed fetch.
      from(
        fetchWorkspace(message.conversation.workspace.id).catch(
          () => undefined,
        ),
      ).pipe(map(() => message)),
    ),
    map((message): SystemNotification => {
      const { meta } = message.conversation
      const text =
        (message.content.type === 'Text' && message.content.text) ||
        'New message'
      return {
        onClick: () => {
          if (meta.type === 'Job') {
            onAction({ jobId: meta.id })
          }
        },
        title: `${message.sender.displayName} (${message.sender.workspace.name})`,
        body: text,
        tag: Id.assert(message.id),
        icon: message.sender.picture,
        timeout: 5000,
      }
    }),
  )
}
